import { useState } from "react";
import { useSelector } from "react-redux";

import { DateTime } from "luxon";
import {
  Typography,
  Box,
  Card,
  Grid2 as Grid,
  Container,
  CardContent,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ScheduledModal from "./modals/ScheduledModal";
import CompletedModal from "./modals/CompletedModal";

const NotaryJobManagement = () => {
  const currentUser = useSelector((state) => state.notarySession.user);
  const loading = false;
  const [scheduledModalOpen, setScheduledModalOpen] = useState(false);
  const [completedModalOpen, setCompletedModalOpen] = useState(false);
  const [selectedScheduledJob, setSelectedScheduledJob] = useState(null);
  const [selectedButton, setSelectedButton] = useState("reminder");
  const [cancelErrorText, setCancelErrorText] = useState("");
  const [rescheduleErrorText, setRescheduleErrorText] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const handleCompletedCloseModal = () => {
    setCompletedModalOpen(false);
  };

  const notaryJobsColumn = [
    {
      field: "booked_date",
      headerName: "Date and Time of Request",
      flex: 1,
      editable: false,
      renderCell: (params) => {
        // Parse the UTC datetime string using Luxon
        const utcDate = DateTime.fromISO(params.value, { zone: "utc" });
        // Convert to US timezone (example: America/New_York)
        const usDate = utcDate.setZone(currentUser?.timezone);
        // Format the date in the desired format
        const formattedDate = usDate.toFormat("MMMM dd, yyyy hh:mm a");
        return <span>{formattedDate}</span>;
      },
    },
    {
      field: "signer",
      headerName: "Signer",
      flex: 0.5,
      editable: false,
    },
    {
      field: "project_address",
      headerName: "Project Address",
      flex: 1,
      editable: false,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.5,
      editable: false,
      renderCell: (params) => {
        // Parse the booked_date in UTC using Luxon
        const bookedDateUtc = DateTime.fromISO(params.row.booked_date, {
          zone: "utc",
        });

        const nowUTC = DateTime.utc();

        const timeDifference = bookedDateUtc - nowUTC;

        // Check if the difference is less than or equal to 5 minutes (300,000 milliseconds)
        if (timeDifference <= 300000) {
          return <Typography variant="body2">RON Started</Typography>;
        } else {
          return <Typography variant="body2">Waiting for RON Start</Typography>;
        }
      },
    },
  ];

  return (
    <>
      {loading ? (
        <>
          <Box
            sx={{
              display: "flex",
              width: "100%",
              height: "100vh",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <CircularProgress color="warning" size="10rem" />
          </Box>
        </>
      ) : (
        <>
          <Container maxWidth="xxl">
            <Grid container spacing={0}>
              <Grid size={{ xs: 12 }} sx={{ mt: 3 }}>
                <Card>
                  <CardContent>
                    <Typography
                      variant={mobileSize ? "h5" : "h4"}
                      align="center"
                    >
                      Scheduled RON Appointments
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12 }} sx={{ padding: 0 }}>
                <Card
                  sx={{
                    backgroundColor: "#FCF2F0",
                  }}
                >
                  <CardContent sx={{ padding: 0 }}>
                    <DataGrid
                      sx={{
                        "& .MuiDataGrid-columnHeaders": {
                          padding: 0, // Adjust as needed
                          margin: 0, // Adjust as needed
                        },
                        "& .MuiDataGrid-columnHeadersInner": {
                          width: "100%", // Adjust as needed
                        },
                        border: "1px solid #FCF2F0", // Change the border color of the entire DataGrid
                        "& .MuiDataGrid-columnHeader": {
                          backgroundColor: "#333333", // Change column header background color
                          color: "white", // Optional: Change text color for contrast
                        },
                        "& .MuiDataGrid-cell": {
                          borderColor: "#333333", // Change cell border color
                        },
                        "& .MuiDataGrid-columnSeparator": {
                          display: "none", // Optional: Hide the column separators
                        },
                        "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                          outline: "none !important",
                        },
                        "& .MuiDataGrid-columnHeader:focus-within": {
                          outline: "none !important",
                        },
                      }}
                      rows={currentUser?.my_current_jobs || []}
                      columns={notaryJobsColumn}
                      getRowId={(row) => row?.id}
                      initialState={{
                        pagination: {
                          paginationModel: {
                            pageSize: 5,
                          },
                        },
                      }}
                      pageSizeOptions={[5, 10, 25, 50, 100]}
                      disableRowSelectionOnClick
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            <ScheduledModal
              open={scheduledModalOpen}
              cancelErrorText={cancelErrorText}
              setCancelErrorText={setCancelErrorText}
              rescheduleErrorText={rescheduleErrorText}
              setRescheduleErrorText={setRescheduleErrorText}
              setScheduledModalOpen={setScheduledModalOpen}
              onClose={() => {}}
              // onConfirm={handleConfirmJob}
              selectedJob={selectedScheduledJob}
              setSelectedScheduledJob={setSelectedScheduledJob}
              jobTakenText={false}
              confirmLoading={false}
              selectedButton={selectedButton}
              setSelectedButton={setSelectedButton}
              cancelLoading={cancelLoading}
              setCancelLoading={setCancelLoading}
              emailLoading={emailLoading}
              setEmailLoading={setEmailLoading}
            />
            <CompletedModal
              currentUser={currentUser}
              open={completedModalOpen}
              cancelErrorText={cancelErrorText}
              setCancelErrorText={setCancelErrorText}
              rescheduleErrorText={rescheduleErrorText}
              setRescheduleErrorText={setRescheduleErrorText}
              setCompletedModalOpen={setCompletedModalOpen}
              onClose={handleCompletedCloseModal}
              // onConfirm={handleConfirmJob}
              selectedJob={[]}
              setSelectedScheduledJob={setSelectedScheduledJob}
              jobTakenText={false}
              confirmLoading={false}
              selectedButton={selectedButton}
              setSelectedButton={setSelectedButton}
              cancelLoading={cancelLoading}
              setCancelLoading={setCancelLoading}
            />
          </Container>
        </>
      )}
    </>
  );
};

export default NotaryJobManagement;
